import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarIcon from "@mui/icons-material/Star";
import { Link } from "react-router-dom";
import _request from "actions/_request";
import i18n from "i18";
import AppContext from "../AppContext";
import AppStoreBillingNotice from "./AppStoreBillingNotice";
import { isAppleBilled } from "../utils/billing";

/**
 * Modal for purchasing additional AI credit packs.
 * Shown when the user has a paid plan but has exhausted their monthly credits.
 *
 * @param {Object}   props              - Component props
 * @param {boolean}  props.open         - Whether the modal is visible
 * @param {Function} props.onClose      - Close callback
 * @param {boolean}  props.isTrial      - If true show upgrade-plan flow instead
 * @param {Object}   [props.shortfall]  - Insufficient-credits payload from a 402
 *   ({ required, available }); when set, an inline upsell banner is shown.
 * @returns {JSX.Element} BuyCreditsModal dialog
 */
export default function BuyCreditsModal({ open, onClose, isTrial = false, shortfall = null }) {
  const { refreshCredits, metaData } = useContext(AppContext);
  // App Store-billed plans top up in the iOS app; the server refuses web packs.
  const appleBilled = isAppleBilled(metaData);

  // Only claim credits are "exhausted" when they actually are; otherwise present
  // top-ups as an optional add-on (they stack on the monthly allowance).
  const monthlyRemaining = metaData?.ai_credits?.monthly_remaining;
  const isExhausted =
    typeof monthlyRemaining === "number" ? monthlyRemaining <= 0 : false;

  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(null); // pack id currently being purchased
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && !isTrial && !appleBilled) {
      setLoading(true);
      setError(null);
      _request.aiCredits
        .getPacks()
        .then((res) => {
          if (res?.isOk && Array.isArray(res.data)) {
            setPacks(res.data);
          } else {
            setError(i18n.t("aiCredits.errorLoadingPacks"));
          }
        })
        .finally(() => setLoading(false));
    }
  }, [open, isTrial, appleBilled]);

  const handleBuy = async (packId) => {
    setBuying(packId);
    setError(null);

    const res = await _request.aiCredits.buy(packId);

    if (res?.isOk && res?.data) {
      window.location.href = res.data;
    } else {
      setError(res?.error || i18n.t("aiCredits.errorBuying"));
      setBuying(null);
    }

    if (refreshCredits) refreshCredits();
  };

  const paperSx = {
    backgroundColor: "var(--background-menu)",
    backgroundImage: "none",
  };

  const headerSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: "16px 24px",
    borderBottom: "1px solid var(--border-color)",
  };

  // Inline upsell banner shown when the modal was opened from an insufficient
  // (402) response — tells the user exactly how short they are.
  const shortfallBanner =
    shortfall &&
    (typeof shortfall.required === "number" ||
      typeof shortfall.available === "number") ? (
      <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.25 }}>
          {i18n.t("aiCredits.shortfallTitle")}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          {i18n.t("aiCredits.shortfallBody", {
            required: shortfall.required ?? 0,
            available: shortfall.available ?? 0,
          })}
        </Typography>
      </Alert>
    ) : null;

  if (isTrial) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: paperSx }}>
        <Box sx={headerSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: "var(--accent-primary)" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "var(--text-color)" }}>
              {i18n.t("aiCredits.upgradeTitle")}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "var(--text-color)" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: "24px", textAlign: "center" }}>
          {shortfallBanner}
          <AutoAwesomeIcon sx={{ fontSize: 48, color: "var(--accent-primary)", mb: 2 }} />
          <Typography sx={{ color: "var(--text-color)", mb: 1 }}>
            {i18n.t("aiCredits.upgradeDescription")}
          </Typography>
          <Button
            component={Link}
            to="/app/pricing"
            variant="contained"
            onClick={onClose}
            sx={{
              mt: 2,
              backgroundColor: "var(--accent-primary)",
              color: "var(--text-color-reverse)",
              "&:hover": { opacity: 0.85, backgroundColor: "var(--accent-primary)" },
            }}>
            {i18n.t("aiCredits.upgradeCta")}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: paperSx }}>
      <Box sx={headerSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: "var(--accent-primary)" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "var(--text-color)" }}>
            {i18n.t("aiCredits.buyTitle")}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "var(--text-color)" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: "24px" }}>
        {shortfallBanner}
        {appleBilled ? (
          <AppStoreBillingNotice variant="credits" sx={{ mb: 0 }} />
        ) : (
        <>
        <Typography
          variant="body2"
          sx={{ color: "var(--text-color)", mb: 3, opacity: 0.8 }}>
          {isExhausted
            ? i18n.t("aiCredits.buyDescription")
            : i18n.t("aiCredits.buyDescriptionTopUp")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "var(--accent-primary)" }} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {packs.map((pack) => (
              <Box
                key={pack.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "16px 20px",
                  borderRadius: "10px",
                  border: "1px solid var(--border-color)",
                  backgroundColor: "var(--hedear-bg)",
                  position: "relative",
                }}>
                {pack.badge && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: "12px !important", color: "var(--text-color-reverse) !important" }} />}
                    label={pack.badge}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: 12,
                      backgroundColor: "var(--accent-primary)",
                      color: "var(--text-color-reverse)",
                      fontWeight: 700,
                      fontSize: 11,
                    }}
                  />
                )}
                <Box>
                  <Typography sx={{ color: "var(--text-color)", fontWeight: 600 }}>
                    {pack.label}
                  </Typography>
                  <Typography sx={{ color: "var(--accent-primary)", fontSize: 13, mt: 0.5 }}>
                    {pack.credits} {i18n.t("aiCredits.credits")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography sx={{ color: "var(--text-color)", fontWeight: 700, fontSize: 18 }}>
                    {pack.price_formatted}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!!buying}
                    onClick={() => handleBuy(pack.id)}
                    startIcon={buying === pack.id ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{
                      backgroundColor: "var(--accent-primary)",
                      color: "var(--text-color-reverse)",
                      "&:hover": { opacity: 0.85, backgroundColor: "var(--accent-primary)" },
                      "&.Mui-disabled": { backgroundColor: "var(--accent-primary)", opacity: 0.4 },
                      minWidth: 90,
                    }}>
                    {buying === pack.id ? i18n.t("aiCredits.buying") : i18n.t("aiCredits.buy")}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
